import React, { Fragment, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import {  Grid, Typography, Button, List, ListItem, Divider,  CircularProgress,
	  Dialog, AppBar, Toolbar, Modal, Backdrop } from '@material-ui/core';
import { TrainerContext } from '../../contexts/TrainerContext.js';
import firebase from '../../Firebase.js';
import HeaderBar from '../HeaderBar.js';
import MainNavigation from '../MainNavigation.js';
import Exam from './Exam.js';
import { ExamStatus, ReportStatus, BottomMessage, Transition, isObjEmpty } from '../../Common.js';
import { formatRelative } from 'date-fns';
import { es, ru, sv, lt, enUS,  } from 'date-fns/locale';

const lmap = {lt, es, ru, sv, en: enUS};

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      container: {
	overflow: 'auto',
	'-webkit-overflow-scrolling': 'touch',
	marginTop: theme.spacing(1),
	[theme.breakpoints.up('sm')] : {
	  height: `calc(100% - 64px)`
	},
	[theme.breakpoints.down('xs')] : {
	  height: `calc(100% - 56px)`
	},
      },
      editExamContainer: {
	[theme.breakpoints.up('sm')] : {
	  height: `calc(100% - 64px)`
	},
	[theme.breakpoints.down('xs')] : {
	  height: `calc(100% - 56px)`
	},
      },
      thumb: {
	height: 100,
      },
      loadMoreBtn: {
	marginTop: theme.spacing(2),
	background: theme.palette.primary.yellow,
	color: theme.palette.primary.brown,
	'&:hover': {
	  background: theme.palette.primary.yellow,
	},
      },
      loader: {
	color: theme.palette.primary.brown,
      }
    }),
);

const pageSize = 5;

const EditExam = ({ exam, updateExams, close }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <Dialog fullScreen
	    open={!isObjEmpty(exam)}
	    onClose={()=>{}}
	    TransitionComponent={Transition}>
      <AppBar position='static'>
	<Toolbar disableGutters>
	  <Grid container>
	    <Grid item xs={3}>
	      <Button color='secondary'
		      onClick={close}>
		{ t('close') }
	      </Button>
	    </Grid>
	    <Grid item xs={6}>
	      <Typography noWrap align='center' variant='h6' >
		{ t('edit-finished-exam') }
	      </Typography>
	    </Grid>
	    <Grid item xs={3}>
	    </Grid>
	  </Grid>
	</Toolbar>
      </AppBar>
      <div className={classes.editExamContainer}>
	<Exam examData={exam}
	      gettingExam={false}
	      editCb={updateExams}/>
      </div>
    </Dialog>
  );
};

export default () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [gettingExams, setGettingExams] = useState(false);
  const [exams, setExams] = useState([]);
  const [page, setPage] = useState(1);
  const [unsubscribe, setUnsubscribe] = useState(_ => _ => {});
  const [hideLoadMore, setHideLoadMore] = useState(false);
  const [bottomMsg, setBottomMsg] = useState(null);
  const [pendingExamNoEdit, setPendingExamNoEdit] = useState(null);
  const [exam, setExam] = useState({});
  const { tricks } = useContext(TrainerContext);

  const setExamToEdit = exam => _ => {
    if (exam.status === 2) {
      setPendingExamNoEdit(t('user-has-resubmitted-your-evaled-exam-you-cannot-edit-it-it-is-in-pending-exams-ready-for-eval'));
      return;
    }
    setExam(exam);
  };

  const updateExams = (updatedFields) => {
    setExams(old => old.map(e => (e.examId !== exam.examId) ? e : { ...e, ...updatedFields }));
    setExam({});
  };

  const getTrick = (trickId) => {
	  const targetLang = i18n.language === 'en-US' ? 'en' : i18n.language;
	  let trick = tricks.filter((item) => item.id === trickId && item.locale === targetLang)
	  return trick[0]
  }

  const showStatus = (status) => {
    switch (status) {
    case ExamStatus.REJECTED:
      return t('rejected');
    case ExamStatus.APPROVED:
      return t('approved');
    case ExamStatus.UPLOADING:
      return t('user-is-uploading');
    case ExamStatus.FINISHED:
      return t('finished');
    case ExamStatus.LOCKED:
      return t('locked');
    case ExamStatus.READY_TO_START:
      return t('ready-to-start');
    // case ExamStatus.INAPPROPRIATE:
    //   return t('inappropriate');
    // case ExamStatus.TECHNICAL_ISSUE:
    //   return t('technical_issue');
    case ExamStatus.PENDING:
      return t('evaled-or-flagged-by-you-but-pending-again');
    default:
      return t('no-posible-state');
    }
  };

  const getExams = async () => {
    setGettingExams(true);
    try {
      let query = firebase.firestore().collectionGroup('exams')
	  .where('evaluatedByEmail', '==', firebase.auth().currentUser.email)
	  .orderBy('reviewDate', 'desc')
	  .limit(page * pageSize);
      await unsubscribe();
      const newUnsubscribe = query.onSnapshot(snapshot => {
	if (snapshot.empty) {
	  setHideLoadMore(true);
	  setGettingExams(false);
	  setLoading(false);
	  setBottomMsg(t('no-exams-found'));
	  return;
	}
	const docs = [];
	snapshot.forEach(d => {
	  docs.push({...d.data(), docPath: d.ref.path});
	});
	setUnsubscribe(_ => newUnsubscribe);
	setExams(docs);
	setPage(s => s += 1);
	setLoading(false);
	setGettingExams(false);
      });
    }
    catch(err) {
      setLoading(false);
      console.log(err);
    }
  };

  useEffect(() => {
    getExams();
  }, []);

  useEffect(_ => {
    return unsubscribe;
  }, [unsubscribe]); 

  return (
    <div style={{ height: '100%' }}>
      <HeaderBar LComp={<MainNavigation />}
		 MComp={<Typography noWrap variant='h6' align='center'>{t('my-finished-exams')}</Typography>}
		 RComp={<div />}/>
      <Grid className={classes.container}>
	{
	  loading
	    ? <Modal open={true}
		     disableAutoFocus
		     disableEnforceFocus
		     BackdropComponent={Backdrop}
		     BackdropProps={{invisible: true}}>
		<Grid container justify='center' alignItems='center' style={{height: '100%'}}>
		  <CircularProgress color='secondary'/>
		</Grid>
	    </Modal>
	  :
	  (() => {
	    if (exams.length === 0) {
	      return (
		<Grid className={classes.container} container justify='center' alignItems='center'>
		  <Typography variant='h2'>
		    {t('no-exams')}
		  </Typography>
		</Grid>
	      );
		}
		if (Object.keys(tricks).length === 0) {
		return (
		<Modal open={true}
		     disableAutoFocus
		     disableEnforceFocus
		     BackdropComponent={Backdrop}
		     BackdropProps={{invisible: true}}>
		<Grid container justify='center' alignItems='center' style={{height: '100%'}}>
		  <CircularProgress color='secondary'/>
		</Grid>
	    </Modal>
		)	
		}
	    return (
	      <List className={classes.container}>
		{exams.map(e => {
		  return (
		    <Fragment key={e.examId}>
		      <ListItem button onClick={setExamToEdit(e)}>
			<Grid container alignItems='center' spacing={1}>
			  <Grid item xs={3}>
			    <img alt='video-thumb'
				 style={{width: '70px', height: 'auto'}}
				 src={e.videoThumbnailUrl} className={classes.thumb}/>
			  </Grid>
			  <Grid item xs={9} >
			    <Grid container direction='column'>
			      <Grid item>
				<Typography variant='h6'>
				  { e.dogName || t('no-name') + ' '} <img alt={e.userLocale + ' flag'}
									  height='20'
									  style={{marginLeft: '10px'}}
									  src={`/img/${e.userLocale}.svg`}/>
				</Typography>
			      </Grid>
			      <Grid item>
				<Typography variant='subtitle2'>
				  <span style={e.status === ExamStatus.APPROVED || e.status === ExamStatus.FINISHED
					       ? {background: '#1ce3b6', color: 'white', padding: '3px'}
					       : {background: '#f62152', color: 'white', padding: '3px'}}>
				    {showStatus(e.status)}
				  </span>
				</Typography>
			      </Grid>
			      <Grid item>
				{
				  e.reportStatus >= 0 &&
				    <Typography variant='subtitle2' style={{marginTop: '3px'}}>
				      <span style={{background: '#f62152', color: 'white', padding: '3px'}}>
					{ e.reportStatus === ReportStatus.INAPPROPRIATE ? t('inappropriate') : t('technical_issue') }
				      </span>
				    </Typography>
				}
			      </Grid>
			      <Grid item>
				<Typography variant='body2'>
				  { t('trick-name') + ': ' + getTrick(e.trickId).name }
				</Typography>
			      </Grid>
			      <Grid item>
				<Typography variant='body2'>
				  { e.isDogPremium ? t('premium') : t('not-premium')}
				</Typography>
			      </Grid>
			      <Grid item>
				<Typography variant='caption'>
				  {/* TODO: remove this after testing */}
				  { t('review-date') + ': '
				    + ((!e.reviewDate || e.reviewDate._seconds) ? 'bad-date' : formatRelative(e.reviewDate.toDate(), new Date(), {locale: lmap[i18n.language]}))
				  }

				</Typography>
			      </Grid>

			    </Grid>
			  </Grid>
			</Grid>
		      </ListItem>
		      <Divider/>
		    </Fragment>
		  );
		})}
		<Grid container justify='center'>
		  <Button onClick={getExams}
			  disabled={gettingExams || hideLoadMore}
			  variant='contained'
			  className={classes.loadMoreBtn}>
		    { gettingExams ? (<CircularProgress size={20} className={classes.loader}/>) : t('load-more') }
		  </Button>
		</Grid>
	      </List>
	    );
	  })()
	}
	<EditExam exam={exam}
		  close={() => setExam({})}
		  updateExams={updateExams}/>
	<BottomMessage open={!!bottomMsg}
		       message={bottomMsg}
		       close={() => setBottomMsg(null)}/>
	<BottomMessage open={!!pendingExamNoEdit}
		       message={pendingExamNoEdit}
		       close={() => setPendingExamNoEdit(null)}/>
      </Grid>
    </div>
  );
};
